import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ChevronDown, ChevronRight, Edit2, Trash2, Plus, GripVertical } from 'lucide-react';
import SortableOption from './SortableOption';
import OptionInlineForm from './OptionInlineForm';
import InlineForm from './InlineForm';

const SortableQuestion = ({
  question,
  bandId,
  questionSetId,
  expanded,
  onToggle,
  handleOptionReorder,
  activeForm,
  setActiveForm,
  editingItem,
  setEditingItem,
  saveOption,
  deleteItem,
  toggleActive,
  sensors
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-white rounded-lg p-3 border border-indigo-200 hover:border-indigo-400 transition-all shadow-sm">
      <div className="flex items-start gap-2">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-indigo-600 mt-1">
          <GripVertical size={18} />
        </div>
        
        <button onClick={onToggle} className="text-indigo-600 hover:bg-indigo-50 p-1 rounded transition-colors mt-0.5">
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <p className="font-semibold text-gray-800 text-sm leading-relaxed">{question.text}</p>
              {question.help_text && (
                <p className="text-xs text-gray-500 mt-1 italic">{question.help_text}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                {question.multi_select && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded">Multi-select</span>
                )}
                <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                  question.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {question.is_active ? 'Active' : 'Inactive'}
                </span>
                <span className="text-xs text-gray-500">{question.options?.length || 0} Options</span>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => toggleActive('question', { bandId, questionSetId, questionId: question.id })}
                className="p-1 hover:bg-indigo-50 rounded transition-colors text-xs"
              >
                {question.is_active ? '👁️' : '👁️‍🗨️'}
              </button>
              <button
                onClick={() => { setEditingItem(question); setActiveForm(`question-${bandId}-${questionSetId}`); }}
                className="p-1 hover:bg-indigo-50 text-indigo-600 rounded transition-colors"
              >
                <Edit2 size={14} />
              </button>
              <button
                onClick={() => deleteItem('question', { bandId, questionSetId, questionId: question.id })}
                className="p-1 hover:bg-red-50 text-red-600 rounded transition-colors"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          {expanded && (
            <div className="mt-3 pl-6 space-y-2">
              <div className="flex justify-between items-center">
                <h6 className="text-xs font-semibold text-gray-700">Answer Options</h6>
                <button
                  onClick={() => { setActiveForm(`option-${bandId}-${questionSetId}-${question.id}`); setEditingItem(null); }}
                  className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold hover:bg-purple-200 transition-colors flex items-center gap-1"
                >
                  <Plus size={12} /> Add Option
                </button>
              </div>

              {activeForm === `option-${bandId}-${questionSetId}-${question.id}` && (
                <InlineForm
                  title={editingItem ? 'Edit Option' : 'Add New Option'}
                  onCancel={() => { setActiveForm(null); setEditingItem(null); }}
                  compact
                >
                  <OptionInlineForm
                    option={editingItem}
                    questionId={question.id}
                    onSave={(data) => saveOption(bandId, questionSetId, question.id, data)}
                    onCancel={() => { setActiveForm(null); setEditingItem(null); }}
                  />
                </InlineForm>
              )}

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(e) => handleOptionReorder(bandId, questionSetId, question.id, e)}
              >
                <SortableContext
                  items={question.options?.map((o) => o.id) || []}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-1.5">
                    {question.options?.map((option) => (
                      <SortableOption
                        key={option.id}
                        option={option}
                        bandId={bandId}
                        questionSetId={questionSetId}
                        questionId={question.id}
                        onEdit={() => { setEditingItem(option); setActiveForm(`option-${bandId}-${questionSetId}-${question.id}`); }}
                        onDelete={() => deleteItem('option', { bandId, questionSetId, questionId: question.id, optionId: option.id })}
                        onToggleActive={() => toggleActive('option', { bandId, questionSetId, questionId: question.id, optionId: option.id })}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {(!question.options || question.options.length === 0) && activeForm !== `option-${bandId}-${questionSetId}-${question.id}` && (
                <div className="text-center py-3 bg-gray-50 rounded">
                  <p className="text-gray-500 text-xs">No options yet. Add your first one!</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SortableQuestion;